import React, { Suspense } from 'react';

import { EmbedForm, FormSkeleton, OverlayElement } from 'donation-elements';
import HostedPagePreviewFrame from './HostedPagePreviewFrame';
import { DUMMY_DONOR_LIST_PREVIEW } from './previewPlaceholders';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import { FEATURE } from '../../utils/entitlements';
import FormPreviewWorkspaceShell from '../../components/workspaces/forms/FormPreviewWorkspaceShell';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const previewTabs = [
  { name: 'Embed Form', current: true },
  { name: 'Hosted Page', current: false },
  { name: 'Overlay', current: false },
];

/**
 * @typedef {Object} FundraiserFormPreviewProps
 * @property {string} [formName]
 * @property {string} [image]
 * @property {string} [color]
 * @property {string} [convertedContent]
 * @property {Array} [customTickets]
 * @property {Array} [customFAQs]
 * @property {Array} [customInputs]
 * @property {string} activePreviewTab
 * @property {Function} setActivePreviewTab
 * @property {string} [merchantName]
 * @property {string} [elementID]
 * @property {string} [eventDate]
 * @property {string} [eventStartTime]
 * @property {string} [eventEndTime]
 * @property {string} [eventLocation]
 * @property {string} [defaultColor]
 * @property {boolean} [showFundraisingBar]
 * @property {string} [targetAmount]
 * @property {string} [amountOffset]
 * @property {string} [countOffset]
 * @property {string} [trackingMetric]
 * @property {string} [metricLabel]
 * @property {number} [subscriptionCount]
 * @property {number} [totalTransactionAmount]
 * @property {Object} [donorListData]
 * @property {Array} [donorListData.recentDonors]
 * @property {Array} [donorListData.topDonors]
 * @property {boolean} [enableEmbedForm]
 * @property {boolean} [showAnonymousDonations]
 * @property {boolean} [showOrganizationNameField]
 * @property {boolean} [active]
 * @property {Array} [funds]
 * @property {Array} [frequencies]
 * @property {Array} [oneTimeAmounts]
 * @property {Array} [dailyAmounts]
 * @property {Array} [weeklyAmounts]
 * @property {Array} [monthlyAmounts]
 * @property {Array} [annuallyAmounts]
 * @property {string} [defaultFrequency]
 * @property {number} [defaultOneTimeAmount]
 * @property {number} [defaultDailyAmount]
 * @property {number} [defaultWeeklyAmount]
 * @property {number} [defaultMonthlyAmount]
 * @property {number} [defaultAnnuallyAmount]
 * @property {string} [formType]
 * @property {Array} [crowdfundingOptions] - Legacy (pre-Phase 4)
 * @property {Array} [productGroups] - Legacy (pre-Phase 4)
 * @property {Array} [oneTimeCrowdfundingOptions] - Phase 4 per-frequency giving levels
 * @property {Array} [dailyCrowdfundingOptions] - Phase 4 per-frequency giving levels
 * @property {Array} [weeklyCrowdfundingOptions] - Phase 4 per-frequency giving levels
 * @property {Array} [monthlyCrowdfundingOptions] - Phase 4 per-frequency giving levels
 * @property {Array} [annuallyCrowdfundingOptions] - Phase 4 per-frequency giving levels
 * @property {Array} [oneTimeProductGroups] - Phase 4 per-frequency sponsorships
 * @property {Array} [dailyProductGroups] - Phase 4 per-frequency sponsorships
 * @property {Array} [weeklyProductGroups] - Phase 4 per-frequency sponsorships
 * @property {Array} [monthlyProductGroups] - Phase 4 per-frequency sponsorships
 * @property {Array} [annuallyProductGroups] - Phase 4 per-frequency sponsorships
 * @property {boolean} [enableDonorList]
 * @property {string} [donorListType]
 * @property {string} [fundraisingBarElementID]
 * @property {string} [donorListElementID]
 * @property {boolean} [showScheduleDonation]
 * @property {Object} [defaultScheduledRange]
 * @property {boolean} [defaultScheduledRange.enabled]
 * @property {string} [defaultScheduledRange.startDate]
 * @property {string} [defaultScheduledRange.endDate]
 * @property {string} [defaultScheduledRange.scheduledTime]
 * @property {boolean} [studioMode]
 * @property {boolean} [isExpanded]
 * @property {Function} [onToggleExpanded]
 */

// Form Preview Section Component
/** @type {React.FC<FundraiserFormPreviewProps>} */
const FundraiserFormPreview = ({
  formName,
  image,
  color,
  convertedContent,
  customTickets,
  customFAQs,
  customInputs = [],
  activePreviewTab,
  setActivePreviewTab,
  merchantName,
  elementID,
  eventDate,
  eventStartTime,
  eventEndTime,
  eventLocation,
  defaultColor = '#9A9285',
  fundraisingBarElementID,
  donorListElementID,
  showFundraisingBar = false,
  targetAmount = '',
  amountOffset = '',
  countOffset = '',
  trackingMetric = 'DOLLAR_AMOUNT',
  metricLabel = 'Sustainers',
  subscriptionCount = 0,
  totalTransactionAmount = 0,
  donorListData = null,
  enableEmbedForm = true,
  showAnonymousDonations = false,
  showOrganizationNameField = false,
  active = true,
  funds = [],
  frequencies = [],
  oneTimeAmounts = [],
  dailyAmounts = [],
  weeklyAmounts = [],
  monthlyAmounts = [],
  annuallyAmounts = [],
  defaultFrequency = 'One-Time',
  defaultOneTimeAmount = 0,
  defaultDailyAmount = 0,
  defaultWeeklyAmount = 0,
  defaultMonthlyAmount = 0,
  defaultAnnuallyAmount = 0,
  formType = 'EMBED_FORM',
  // Legacy fields (backward compatibility with pre-Phase 4 forms)
  crowdfundingOptions = [],
  productGroups = [],
  // NEW: Per-frequency giving levels (Phase 4 - January 2026)
  oneTimeCrowdfundingOptions = [],
  dailyCrowdfundingOptions = [],
  weeklyCrowdfundingOptions = [],
  monthlyCrowdfundingOptions = [],
  annuallyCrowdfundingOptions = [],
  // NEW: Per-frequency sponsorships (Phase 4 - January 2026)
  oneTimeProductGroups = [],
  dailyProductGroups = [],
  weeklyProductGroups = [],
  monthlyProductGroups = [],
  annuallyProductGroups = [],
  enableDonorList = false,
  donorListType = 'RECENT_DONORS',
  showAllSupportersAnonymous = false,
  showScheduleDonation = false,
  defaultScheduledRange = {}, // Per-frequency config (FrequencyScheduledRangeMap)
  studioMode = false,
  isExpanded = false,
  onToggleExpanded,
}) => {
  // PE-1630 — the Overlay preview tab is gated on `overlay_popup` (Base+), the
  // same entitlement that hides the in-drawer Overlay Trigger card and the
  // forms-row "Overlay URL" action. Without this, the tab is a side door that
  // lets a non-entitled org preview/use overlay regardless of plan. Fail-open
  // when entitlement resolution is degraded (UX guard; server stays authority).
  const { can: canEntitle, degraded: entDegraded } = useEntitlements();
  const canOverlay = entDegraded || canEntitle(FEATURE.OVERLAY_POPUP);
  const visiblePreviewTabs = canOverlay
    ? previewTabs
    : previewTabs.filter((tab) => tab.name !== 'Overlay');

  // Helper function to convert frequency names to StandardForm format
  const convertFrequencyToBackendFormat = (freq) => {
    const mapping = {
      'One-Time': 'ONE_TIME',
      Daily: 'DAILY',
      Weekly: 'WEEKLY',
      Monthly: 'MONTHLY',
      Annually: 'ANNUALLY',
    };
    return mapping[freq] || freq;
  };

  // Check if we have valid data to render
  // For new forms (no elementID, 'preview', or 'NEW_FORM'), always render immediately with defaults
  // For existing forms (valid UUID), wait until elementID and formName are loaded from backend
  const isNewForm = !elementID || elementID === 'preview' || elementID === 'NEW_FORM';
  const isDataReady = isNewForm || (elementID && formName);

  // Create preview data object that matches the EmbedForm/Base data structure
  const fundraiserPreviewData = React.useMemo(() => {
    // Convert frequency names from dashboard format to StandardForm format
    const convertedFrequencies =
      frequencies.length > 0 ? frequencies.map(convertFrequencyToBackendFormat) : [];

    const convertedDefaultFrequency = convertFrequencyToBackendFormat(defaultFrequency);

    // Always return data, even without elementID (for new form previews)
    return {
      elementID: elementID || 'preview',
      elementName: formName || 'Enter Form Title',
      formName: formName || 'Enter Form Title',
      fundraisingBarElementID: fundraisingBarElementID || '',
      donorListElementID: donorListElementID || '',
      elementType: formType || 'EMBED_FORM',
      formType: formType,
      lightColor: color || defaultColor,
      darkColor: color || defaultColor,
      nonprofitName: merchantName || 'Your Organization',
      image: image || null,
      details: convertedContent || '',
      customInputs: customInputs || [],
      formFaqs: customFAQs || [],
      showAnonymousDonations: showAnonymousDonations,
      showOrganizationNameField: showOrganizationNameField,
      active: active,
      showFundraisingBar: showFundraisingBar,
      targetAmount: targetAmount || '0',
      amountOffset: amountOffset || '0',
      countOffset: countOffset || '0',
      elementOffset: parseInt(countOffset) || 0,
      trackingMetric: trackingMetric || 'DOLLAR_AMOUNT',
      metricLabel: metricLabel || 'Sustainers',
      subscriptionCount: subscriptionCount ?? 0,
      totalTransactionAmount: totalTransactionAmount || 0,
      enableEmbedForm: enableEmbedForm,
      enableDonorList: enableDonorList,
      donorListType: donorListType,
      // Fundraiser-specific fields
      funds: funds || [],
      frequencies: convertedFrequencies,
      defaultFrequency: convertedDefaultFrequency,
      oneTimeAmounts: oneTimeAmounts.length > 0 ? oneTimeAmounts : [25, 50, 100, 250],
      dailyAmounts: dailyAmounts || [],
      weeklyAmounts: weeklyAmounts || [],
      monthlyAmounts: monthlyAmounts || [],
      annuallyAmounts: annuallyAmounts || [],
      defaultOneTimeAmount: defaultOneTimeAmount ?? 0,
      defaultDailyAmount: defaultDailyAmount ?? 0,
      defaultWeeklyAmount: defaultWeeklyAmount ?? 0,
      defaultMonthlyAmount: defaultMonthlyAmount ?? 0,
      defaultAnnuallyAmount: defaultAnnuallyAmount ?? 0,
      // Additional required fields for StandardForm
      showScheduleDonation: showScheduleDonation || false,
      cardMin: 1,
      defaultPaymentProcessor: 'STRIPE',
      maxTransactionAmount: 999999.99,
      showAddToCart: false,
      // Legacy fields (backward compatibility with pre-Phase 4 forms)
      crowdfundingOptions: crowdfundingOptions || [],
      productGroups: productGroups || [],
      // NEW: Per-frequency giving levels (Phase 4 - January 2026)
      oneTimeCrowdfundingOptions: oneTimeCrowdfundingOptions || [],
      dailyCrowdfundingOptions: dailyCrowdfundingOptions || [],
      weeklyCrowdfundingOptions: weeklyCrowdfundingOptions || [],
      monthlyCrowdfundingOptions: monthlyCrowdfundingOptions || [],
      annuallyCrowdfundingOptions: annuallyCrowdfundingOptions || [],
      // NEW: Per-frequency sponsorships (Phase 4 - January 2026)
      oneTimeProductGroups: oneTimeProductGroups || [],
      dailyProductGroups: dailyProductGroups || [],
      weeklyProductGroups: weeklyProductGroups || [],
      monthlyProductGroups: monthlyProductGroups || [],
      annuallyProductGroups: annuallyProductGroups || [],
      defaultScheduledRange: defaultScheduledRange || {},
    };
  }, [
    elementID,
    formName,
    color,
    image,
    defaultColor,
    merchantName,
    convertedContent,
    customInputs,
    customFAQs,
    showAnonymousDonations,
    showOrganizationNameField,
    active,
    showFundraisingBar,
    targetAmount,
    amountOffset,
    countOffset,
    trackingMetric,
    metricLabel,
    subscriptionCount,
    totalTransactionAmount,
    enableEmbedForm,
    enableDonorList,
    donorListType,
    funds,
    frequencies,
    oneTimeAmounts,
    dailyAmounts,
    weeklyAmounts,
    monthlyAmounts,
    annuallyAmounts,
    defaultFrequency,
    defaultOneTimeAmount,
    defaultDailyAmount,
    defaultWeeklyAmount,
    defaultMonthlyAmount,
    defaultAnnuallyAmount,
    formType,
    // Legacy dependencies
    crowdfundingOptions,
    productGroups,
    // NEW: Per-frequency dependencies (Phase 4)
    oneTimeCrowdfundingOptions,
    dailyCrowdfundingOptions,
    weeklyCrowdfundingOptions,
    monthlyCrowdfundingOptions,
    annuallyCrowdfundingOptions,
    oneTimeProductGroups,
    dailyProductGroups,
    weeklyProductGroups,
    monthlyProductGroups,
    annuallyProductGroups,
    fundraisingBarElementID,
    donorListElementID,
    showScheduleDonation,
    defaultScheduledRange,
  ]);
  const previewContent =
    activePreviewTab === 'Embed Form' ? (
      <Suspense fallback={<div>Loading fundraiser form...</div>}>
        <div
          data-cs-position="donor"
          // CON-42: donor components mounted in the dashboard's document resolve every
          // --cs-* against dashboard.css unless a scope says otherwise. Applies
          // donor-scope.css to this subtree only; the dashboard's own chrome is
          // untouched. Content a donor component portals to document.body escapes
          // this — see decisions.md D-previewer-scope.
          className={studioMode ? 'py-3' : 'py-4'}
          style={{
            zoom: 0.7,
            maxWidth: '100%',
          }}
        >
          {/* Only render EmbedForm when we have valid data (elementID + formName) to prevent flicker */}
          {/* This ensures all form data has been populated before rendering */}
          {/* The preview mounts the donor brand provider for the same reason the
              widget's own entities do: the donor controls inside read brand
              through scoped custom properties as well as through props, so
              without it this preview renders the unbranded fallback of each one
              and stops matching the form it is previewing (CON-157). */}
          {isDataReady ? (
            <EmbedForm
              formID={elementID}
              brandColors={color}
              isDrawerOpen={false}
              mountBrandProvider
              backendUrl={import.meta.env.VITE_ELEMENTS_BACKEND_URL}
              previewMode={true}
              previewData={fundraiserPreviewData}
            />
          ) : (
            <FormSkeleton
              formType={formType || 'EMBED_FORM'}
              brandColors={color}
              isDrawerOpen={false}
            />
          )}
        </div>
      </Suspense>
    ) : canOverlay && activePreviewTab === 'Overlay' ? (
      <Suspense fallback={<div>Loading overlay preview...</div>}>
        <div
          data-cs-position="donor"
          // CON-42: donor components mounted in the dashboard's document resolve every
          // --cs-* against dashboard.css unless a scope says otherwise. Applies
          // donor-scope.css to this subtree only; the dashboard's own chrome is
          // untouched. Content a donor component portals to document.body escapes
          // this — see decisions.md D-previewer-scope.
          style={{
            zoom: 0.55,
            maxWidth: '100%',
          }}
        >
          {isDataReady ? (
            <OverlayElement
              elementID={elementID || 'preview'}
              backendUrl={import.meta.env.VITE_ELEMENTS_BACKEND_URL}
              previewMode={true}
              previewData={fundraiserPreviewData}
            />
          ) : (
            <FormSkeleton
              formType={formType || 'EMBED_FORM'}
              brandColors={color}
              isDrawerOpen={false}
            />
          )}
        </div>
      </Suspense>
    ) : (
      <HostedPagePreviewFrame
        previewData={{
          formName,
          image,
          color: color || defaultColor,
          convertedContent,
          customTickets,
          customFAQs,
          customInputs,
          merchantName,
          elementID,
          eventDate,
          eventStartTime,
          eventEndTime,
          eventLocation,
          donorListElementID,
          fundraisingBarElementID,
          showFundraisingBar,
          targetAmount,
          amountOffset,
          elementOffset: parseInt(countOffset) || 0,
          trackingMetric,
          metricLabel,
          subscriptionCount,
          totalTransactionAmount,
          showAnonymousDonations,
          showOrganizationNameField,
          active,
          funds,
          frequencies,
          oneTimeAmounts,
          dailyAmounts,
          weeklyAmounts,
          monthlyAmounts,
          annuallyAmounts,
          defaultFrequency,
          defaultOneTimeAmount: defaultOneTimeAmount ?? 0,
          defaultDailyAmount: defaultDailyAmount ?? 0,
          defaultWeeklyAmount: defaultWeeklyAmount ?? 0,
          defaultMonthlyAmount: defaultMonthlyAmount ?? 0,
          defaultAnnuallyAmount: defaultAnnuallyAmount ?? 0,
          formType,
          // Legacy fields (backward compatibility)
          crowdfundingOptions,
          productGroups,
          // NEW: Per-frequency giving levels (Phase 4)
          oneTimeCrowdfundingOptions,
          dailyCrowdfundingOptions,
          weeklyCrowdfundingOptions,
          monthlyCrowdfundingOptions,
          annuallyCrowdfundingOptions,
          // NEW: Per-frequency sponsorships (Phase 4)
          oneTimeProductGroups,
          dailyProductGroups,
          weeklyProductGroups,
          monthlyProductGroups,
          annuallyProductGroups,
          enableEmbedForm,
          enableDonorList,
          donorListType,
          donorListData: donorListData ?? (enableDonorList ? DUMMY_DONOR_LIST_PREVIEW : null),
          showAllSupportersAnonymous,
          showScheduleDonation,
          defaultScheduledRange,
        }}
      />
    );

  if (studioMode) {
    return (
      <FormPreviewWorkspaceShell
        tabs={visiblePreviewTabs}
        activeTab={activePreviewTab}
        onTabChange={setActivePreviewTab}
        isExpanded={isExpanded}
        onToggleExpanded={onToggleExpanded}
      >
        {previewContent}
      </FormPreviewWorkspaceShell>
    );
  }

  return (
    <div className="flex h-fit flex-1 flex-col bg-paper-muted">
      <div className="m-4 rounded-lg border border-edge bg-paper-inset">
        <nav className="flex w-full p-0.5" aria-label="Preview mode">
          {visiblePreviewTabs.map((tab) => (
            <button
              type="button"
              key={tab.name}
              onClick={() => setActivePreviewTab(tab.name)}
              aria-pressed={tab.name === activePreviewTab}
              className={classNames(
                tab.name === activePreviewTab
                  ? 'border border-edge bg-white text-ink shadow-sm'
                  : 'border border-transparent bg-transparent text-ink-secondary hover:text-ink',
                'flex-1 whitespace-nowrap rounded-md px-2 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage/40',
              )}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>
      <div className="h-full w-full flex-1 overflow-y-hidden">{previewContent}</div>
    </div>
  );
};

export default FundraiserFormPreview;
